import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import { getHtmlCopy } from '../../i18n';
import { Message } from '../utils/Message';
import styles from './CookieNoticeBanner.module.scss';
import { useOverdriveTracking } from './use-overdrive-tracking';

export interface CookieNoticeBannerProps {
    cookieDomain?: string;
    openCookieSettings: () => void;
    onClose?: () => void;
    isOpen: boolean | undefined;
}

export const CookieNoticeBanner = ({
    cookieDomain,
    openCookieSettings,
    onClose,
    isOpen,
}: CookieNoticeBannerProps) => {
    const bannerRef = useRef<HTMLDivElement>(null);
    // isGdprCountry is always false when this component is used
    const [saveDefaultTracking] = useOverdriveTracking(cookieDomain, false);

    const consentAndClose = useCallback(
        (event: UIEvent | React.UIEvent) => {
            const focused = document.activeElement;
            saveDefaultTracking();
            onClose?.();
            // don't reload for implicit consent
            if (
                (event.target as HTMLElement).id !==
                'cookie-banner-close-button'
            ) {
                (focused as HTMLElement)?.focus();
            }
        },
        [onClose, saveDefaultTracking]
    );

    // stops clicks from anywhere in the modal from propagating to the body listener
    const stopPropagation = (event: React.UIEvent) => {
        event.stopPropagation();
    };

    const handleButtonClick = (event: React.UIEvent) => {
        // rely on event bubbling from the <button> in the copy
        if ((event.target as HTMLElement).id === 'banner-cookie-button') {
            onClose?.();
            openCookieSettings();
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleButtonClick(event);
        } else if (event.key === 'Escape') {
            consentAndClose(event);
        } else if (event.key === 'Tab') {
            if (
                !event.shiftKey &&
                event.target ===
                    bannerRef.current?.querySelector('#banner-cookie-button')
            ) {
                event.preventDefault();
                (
                    bannerRef.current?.querySelector(
                        '.modal__x-button'
                    ) as HTMLElement
                )?.focus();
            } else if (
                event.shiftKey &&
                event.target ===
                    bannerRef.current?.querySelector('.modal__x-button')
            ) {
                event.preventDefault();
                (
                    bannerRef.current?.querySelector(
                        '#banner-cookie-button'
                    ) as HTMLElement
                )?.focus();
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.addEventListener('click', consentAndClose);
            (bannerRef.current?.firstElementChild as HTMLElement)?.focus();
        } else {
            document.body.removeEventListener('click', consentAndClose);
        }

        return () => {
            document.body.removeEventListener('click', consentAndClose);
        };
    }, [isOpen, consentAndClose]);

    return isOpen ? (
        <div className={styles.cookieBanner} onClick={stopPropagation}>
            <div
                role="alert"
                className={styles.bannerBody}
                ref={bannerRef}
                onKeyDown={handleKeyDown}
            >
                <button
                    className={clsx(styles.close, 'modal__x-button')}
                    id="cookie-banner-close-button"
                    onClick={consentAndClose}
                    aria-label={getHtmlCopy('close')}
                >
                    ×
                </button>
                <p onClick={handleButtonClick}>
                    <Message messageKey="gdpr.noticeBanner.copy" />
                </p>
            </div>
        </div>
    ) : (
        <></>
    );
};
